import { format, parseISO } from 'date-fns'
import { Dayjs } from 'dayjs'

const BULLET_CHARS = '\u2022\u25CF\u25CB\u25E6\u25AA\u25AB\u2023\u2043'

const BULLET_PATTERN = new RegExp(`[${BULLET_CHARS}]`, 'g')
const LEADING_BULLET_PATTERN = new RegExp(`^[${BULLET_CHARS}]\\s*`)

export const stripLeadingBullet = (text: string): string =>
  text.replace(LEADING_BULLET_PATTERN, '')
/**
 * Normalises Unicode characters that common PDF fonts (Helvetica, Manrope)
 * cannot render — bullet variants, smart quotes, dashes, primes, etc.
 */
export const sanitizeForPdf = (text: string): string =>
  text.replace(BULLET_PATTERN, '\u2022')

export const formatDate = (
  date: Dayjs | string | Date | null | undefined,
  includeTime: boolean = false
): string => {
  if (!date) return 'No date specified'

  if (
    typeof date === 'object' &&
    'format' in date &&
    typeof date.format === 'function'
  ) {
    return includeTime
      ? date.format('MM/DD/YYYY, hh:mm:ss A')
      : date.format('MM/DD/YYYY')
  }

  // Handle ISO string timestamps (signoff)
  if (typeof date === 'string') {
    try {
      const parsedDate = parseISO(date)

      if (isNaN(parsedDate.getTime())) {
        return 'Invalid date'
      }

      return includeTime
        ? format(parsedDate, 'MM/dd/yyyy, hh:mm:ss a')
        : format(parsedDate, 'MM/dd/yyyy')
    } catch (error) {
      console.error('Error parsing date string:', error, date)
      return 'Invalid date'
    }
  }

  if (date instanceof Date) {
    try {
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }

      return includeTime
        ? format(date, 'MM/dd/yyyy, hh:mm:ss a')
        : format(date, 'MM/dd/yyyy')
    } catch (error) {
      console.error('Error formatting Date object:', error, date)
      return 'Invalid date'
    }
  }

  return 'Invalid date'
}

export const formatMeasurement = (
  value: number | undefined,
  unit: string | undefined
): string => {
  if (value === undefined) return 'Not specified'
  return `${value}${unit ? ` ${unit}` : ''}`
}
